// @flow

import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Theme, withTheme, withStyles } from '@material-ui/core';
import { withSnackbar } from 'notistack';
import { withTranslation } from 'react-i18next';
import Toggle from 'react-toggled';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText/ListItemText';
import ISO6391 from 'iso-639-1';
import Collapse from '@material-ui/core/Collapse/Collapse';
import List from '@material-ui/core/List';
import ReactCountryFlag from 'react-country-flag';
import ExpandLessOutlinedIcon from '@material-ui/icons/ExpandLessOutlined';
import ExpandMoreOutlinedIcon from '@material-ui/icons/ExpandMoreOutlined';
import LanguageOutlinedIcon from '@material-ui/icons/LanguageOutlined';

import { styles, EmptyIcon } from './SidebarHelpers';
import { switchLang } from '../../actions/conversation';
import { getLang } from '../../selectors';
import translationLangs from '../../../assets/translation_langs.json';
import { canShowFlagEmoji } from '../../utils/platform';

type Props = {
  t: Function,
  i18n: Object,
  theme: Theme,
  classes: Object,
};

const TranslationSelector = React.memo(function TranslationSelector(props: Props) {
  const { t, classes } = props;
  const lang = useSelector(getLang);
  const dispatch = useDispatch();
  const languageChange = (languageCode: string) => {
    dispatch(switchLang(languageCode));
  };

  const getNativeName = languageCode => {
    // Google API generally allows ISO-639-1 codes, but it also allows
    // two BCP-47 language codes - for two variants of ZH language. Native names
    // for those languages are hard coded here.
    if (languageCode === 'zh_TW') {
      return '國語(台灣)';
    }
    if (languageCode === 'zh_CN') {
      return '普通话(中国大陆)';
    }
    return ISO6391.getNativeName(languageCode);
  };
  const currentLangObject = translationLangs.find(lang_ => lang_.languageCode === lang);
  const currentFlagCode = currentLangObject && currentLangObject.flagCode;
  return (
    <Toggle>
      {({ on, getTogglerProps, toggle }) => (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <div>
            <ListItem button {...getTogglerProps()}>
              <ListItemIcon>
                <LanguageOutlinedIcon />
              </ListItemIcon>
              <ListItemText classes={{ root: classes.listItemText }}>
                {t('sidebar.switchLang')}
                <br />
                <b>
                  {currentFlagCode && (
                    <ReactCountryFlag
                      style={{ marginRight: 5 }}
                      svg={!canShowFlagEmoji()}
                      countryCode={currentFlagCode}
                    />
                  )}
                  {lang === '~' ? `\uD83C\uDF0D \u00A0${t('sidebar.chooseLanguage')}` : getNativeName(lang)}
                </b>
              </ListItemText>
              <ListItemIcon classes={{ root: classes.expandIcon }}>
                {on ? <ExpandLessOutlinedIcon /> : <ExpandMoreOutlinedIcon />}
              </ListItemIcon>
            </ListItem>
            <Collapse in={on}>
              <List>
                {lang !== '~' && (
                  <ListItem key="~" button>
                    <EmptyIcon />
                    <ListItemText
                      classes={{ root: classes.listItemText }}
                      onClick={() => {
                        toggle();
                        languageChange('~');
                      }}
                    >
                      <span role="img" aria-label="cancel">
                        ❌
                      </span>{' '}
                      {t('sidebar.stopTranslation')}
                    </ListItemText>
                  </ListItem>
                )}
                {translationLangs.map(({ languageCode, flagCode }) => (
                  <ListItem key={languageCode} button>
                    <EmptyIcon />
                    <ListItemText
                      classes={{ root: classes.listItemText }}
                      onClick={() => {
                        toggle();
                        languageChange(languageCode);
                      }}
                    >
                      <ReactCountryFlag style={{ marginRight: 5 }} svg={!canShowFlagEmoji()} countryCode={flagCode} />
                      {getNativeName(languageCode)}
                    </ListItemText>
                  </ListItem>
                ))}
              </List>
            </Collapse>
          </div>
        </div>
      )}
    </Toggle>
  );
});

export default withStyles(styles)(withTheme(withSnackbar(withTranslation()(TranslationSelector))));
